import { Link, Typography } from "@mui/material";

import {
  faqs,
  howItWorks,
  createPerkTotalLabel,
  purchaseSeatsLanguages,
  redeemGiftCardLanguages,
  purchaseGiftCardLanguages,
  AnalyticsHeader,
  CheckoutSuccessDescription,
} from "./helpers";
import { routes } from "@/global/routes";
import {
  DbacksTCLanguage,
  DbacksHamburgerTCListItems,
  DbacksAdditionalDetailsDisclaimerTC,
} from "../../tc_helper_dbacks";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";

import type { TeamConfig } from "@/global/hooks/useTeamConfig";

import suiteImage1 from "public/images/diamondbacks/24DB511.jpg";
import suiteImage2 from "public/images/diamondbacks/24DB615.jpg";
import foodImage1 from "public/images/diamondbacks/dbacks_food1.jpg";
import BackgroundImage from "public/images/diamondbacks/dbacks_bg.jpeg";
import PremiumMenu from "public/images/diamondbacks/D-backs Suite Menu.jpg";
import SeatMap3dPreview from "public/images/diamondbacks/seat_map_preview.png";
import LFHRPorchMenu from "public/images/diamondbacks/LF HR Porch Suite Menu.jpg";
import BannerImage from "public/images/diamondbacks/MK05-11241570 Web_Attend_Site_Specs-1486X148_Group_Tickets.jpg";

const porchImage1 =
  "https://ss-leagues.s3.amazonaws.com/AZDBacks%20Assets/2025%20Suites%20Images/HR%20Porch%201.JPG";
const porchImage2 =
  "https://ss-leagues.s3.amazonaws.com/AZDBacks%20Assets/2025%20Suites%20Images/HR%20Porch%202.JPG";
const porchImage3 =
  "https://ss-leagues.s3.amazonaws.com/AZDBacks%20Assets/2025%20Suites%20Images/HR%20Porch%203.jpg";
const porchImage4 =
  "https://ss-leagues.s3.amazonaws.com/AZDBacks%20Assets/2025%20Suites%20Images/HR%20Porch%204.JPG";

const LogoImage = "https://ss-leagues.s3.amazonaws.com/MLB-3/AZD-UPDATED.png";

const config: TeamConfig = {
  teamSchema: "dbacks_groups",
  browserTitle: "Arizona Diamondbacks",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Arizona Diamondbacks",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10167-diamondbacks-sec",
    mapId: "blockmap2d",
    isGroupTicketSeatMapEnabled: true,

    isShowAvailableSeatsButtonHidden: true,
    dvmFallback3dViewSectionId: "S_144",

    suiteImages: [],
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: false,
  inventoryType: inventoryTypeUIDs.groupLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 15,
    defaultSeatCount: 15,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    isPerksEnabled: false,
    hideViewTicketsInThankYouPage: true,
    isTicketTransferEnabled: true,
  },

  oktaClientId: "",
  oktaIssuer: "",
  platform: vendorUUIDs.ticketmaster,
  refreshInventoryWhenEventClick: false,

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader: AnalyticsHeader,
  },

  isSaveCardAllowed: true,

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  authConfigs: {
    isChooseSeatsAuthWithEventCodesDisabled: true,
  },

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/azdbacks/my-events",

  // images
  images: {
    findGamesBannerImage: BannerImage.src,
    authBackgroundImage: BackgroundImage.src,
    checkoutStatusPageImage: BackgroundImage.src,
    giftCardStandalonePageImage: BannerImage.src,
    logoImage: LogoImage + "?width=84&height=66",
    logoNameImage: "",
    faviconImage: LogoImage,
    seatMap3dPreviewImage: SeatMap3dPreview.src,
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },
    bottomBar: {
      showGameSelectionProgress: false,
    },

    inventoryCardSettings: {
      isPBLineItemsVisible: false,
      isParkingPriceVisible: true,
      seatCountLabel: "Tickets Included",
    },
    inventoryContainer: {
      getGridCellHeight: (args) => (args?.mdDown ? 210 : 155),
    },

    perks: {
      hideAddAllButton: true,
      images: {
        foodUpgrade: [foodImage1.src],
      },
    },
    checkoutPerkSubtotals: {
      perkTotalLabel: createPerkTotalLabel,
    },

    gameFiltersSettings: {
      hideAccessibilityRadioButton: true,
    },

    chooseSeatsPreloaderTexts: [
      { text: "Fetching available seats…", duration: 3000 },
      { text: "Huddle up— loading your group’s options…", duration: 3000 },
      { text: "Almost there—gathering seat availability…", duration: 3000 },
    ],

    linkTransferConfirmModal: {
      viewTicketsUrl: "https://bp.mlb.com/Wallet",
    },
  },

  colors: {
    primary: "#AA182C",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <DbacksHamburgerTCListItems />, // can be react node as well
    checkoutTermsAndCondition: <DbacksTCLanguage />,
    registrationTermsAndCondition: <DbacksTCLanguage />,
    additionalDetailsDisclaimerTC: <DbacksAdditionalDetailsDisclaimerTC />,
    checkoutSuccessDescription: <CheckoutSuccessDescription />,
    gameFilters: {
      seatCounter: {
        description:
          "Select the number of people in you group. Minimum of 15 required",
      },
      priceRange: {
        title: "Price Range per Ticket",
      },
      accessibility: {
        description:
          "If you are looking for accessible seating for your group, please contact the box office at 602-462-4600.",
      },
    },
    purchaseSeats: purchaseSeatsLanguages,
    purchaseGiftCard: purchaseGiftCardLanguages,
    redeemGiftCard: redeemGiftCardLanguages,

    checkoutServiceNote: (
      <Typography>
        To pre-purchase parking click{" "}
        <Link href="http://dbacks.com/parking">HERE</Link>. Secure the best spot
        closest to the Ballpark by purchasing in advance.
      </Typography>
    ),

    giftCard: {
      title: "Give The Gift Of Broadway",
      createGiftCard: {
        description:
          "Share the magic of Broadway with a friend or loved one. Upon purchase of a gift card, a unique code will be generated for the intended recipient to redeem. Once redeemed, the gift card is not refundable.",
        recipient: {
          sendNowFalse: {
            description:
              "If an intended recipient is not specified, once purchased, you will have to forward the unique code to the recipient. Upon your recipient receiving their gift card, they will be prompted to enter their unique code to redeem the gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Hippodrome Broadway Series shows in Baltimore.",
          },
          sendNowTrue: {
            description:
              "The Recipient will be sent an email with the unique code for them to redeem their gift card. Once redeemed, the recipient will receive an account credit that they can use to purchase tickets for all Hippodrome Broadway Series shows in Baltimore.",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore account above.",
        },
        logout: {
          description:
            "Please input the case sensitive code provided to redeem the value of the gift card to the Hippodrome Broadway Series in Baltimore.",
        },
        success: {
          description:
            "has been added to your Hippodrome Broadway Series in Baltimore Account Manager account!",
        },
      },
    },
    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
